import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, Checkbox, Modal } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { addToCart } from "../../slices/restaurantCartSlice";
import NumberFormat from "react-number-format";
import RightDrawerTemplate from "../RightDrawerTemplate";
import { Info, ShoppingCart } from "lucide-react";
import { roundDisplayPrice } from "../../utils/priceHelpers";

const MINIMUM_STOCK_VALUE = 0;

export const FoodCard = ({ item, businessType, isOpen = true, openTime }) => {
  const isNextDay = businessType === "next_day" || item?.deliveryDateType === "next_day" || item?.businessType === "next_day";
  const effectiveIsOpen = isNextDay ? true : isOpen;

  const formatCapacityClean = (val) => {
    if (!val || val === "N/a" || val === "n/a") return "";
    const num = parseFloat(val);
    if (isNaN(num)) return String(val);
    return Number.isInteger(num) ? String(num) : String(parseFloat(num.toFixed(2)));
  };

  const { id: paramRestaurantId } = useParams();
  const restaurantId =
    paramRestaurantId ||
    item?.businessId ||
    item?.restaurant_id ||
    item?.restaurantId ||
    item?.business_id ||
    item?.id;
  const discount = parseFloat(item.discount || 0);
  const originalPrice = parseFloat(item.priceWithMarkup || 0);
  const dispatch = useDispatch();
  const formatString = (str) => {
    return (str || "")
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => {
        if (!isNaN(word)) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ");
  };
  const isWholesaleItem = businessType === "raw_food" || businessType === "wholesale";
  const minUnits = 1;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [quantity, setQuantity] = useState(minUnits);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [extraQuantities, setExtraQuantities] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    setQuantity(minUnits);
  }, [item, businessType]);
  const isAvaialable = (item?.availableStock !== undefined
    ? Number(item?.availableStock) > MINIMUM_STOCK_VALUE
    : item?.isAvailable !== false) &&
    (item?.qty_available === undefined || Number(item?.qty_available) > MINIMUM_STOCK_VALUE);

  // Real spice level, or null when the item isn't spicy (backend uses "na").
  const spicyLabel = (() => {
    const s = (item?.spicyLevel || "").trim().toLowerCase();
    if (!s || ["na", "n/a", "none", "not applicable"].includes(s)) return null;
    return formatString(item.spicyLevel);
  })();

  const handleExtraQuantityChange = (extraId, increment) => {
    setExtraQuantities((prevQuantities) => ({
      ...prevQuantities,
      [extraId]: Math.max(1, (prevQuantities[extraId] || 0) + increment),
    }));
  };

  const getContainerExtra = (qty) => {
    if (item.containerCost && parseFloat(item.containerCost) > 0) {
      const portionsPerPack = item.portionsPerPack || 1;
      const numContainers = Math.ceil(qty / portionsPerPack);
      const rawContainerCost = parseFloat(item.containerCost);
      const containerPriceWithMarkup = item.containerCostWithMarkup || item.container_cost_with_markup
        ? parseFloat(item.containerCostWithMarkup || item.container_cost_with_markup)
        : roundDisplayPrice(rawContainerCost / 0.8);
      return {
        id: "container_cost",
        price: containerPriceWithMarkup,
        item: { name: "Container Cost" },
        quantity: numContainers,
      };
    }
    return null;
  };

  const calculateTotalPrice = () => {
    const extrasPrice = selectedExtras.reduce((total, extra) => {
      return total + extra.price * (extraQuantities[extra.id] || 1);
    }, 0);

    return quantity * originalPrice + extrasPrice;
  };

  const handleCheckboxChange = (extraId, price, name) => {
    setSelectedExtras((prev) => {
      const isSelected = prev.find((extra) => extra.id === extraId);
      if (isSelected) {
        setExtraQuantities((prevQuantities) => {
          const newQuantities = { ...prevQuantities };
          delete newQuantities[extraId];
          return newQuantities;
        });
        return prev.filter((extra) => extra.id !== extraId);
      } else {
        setExtraQuantities((prevQuantities) => ({
          ...prevQuantities,
          [extraId]: 1,
        }));
        return [
          ...prev,
          { id: extraId, price, item: { name } },
        ];
      }
    });
  };

  const handleAddToCart = () => {
    const extrasWithQuantities = selectedExtras.map((extra) => ({
      ...extra,
      quantity: extraQuantities[extra.id],
    }));

    const vendorName = item?.rawFood?.business?.name || item?.raw_foods?.business?.name || item?.business?.name || item?.store?.name || item?.restaurant?.name || item?.businessName;

    dispatch(
      addToCart({
        restaurantId,
        productId: item.id,
        quantity,
        public_price: originalPrice,
        discount: parseFloat(item.discount),
        product_name: item.name,
        businessType,
        businessName: vendorName,
        deliveryDateType: item.deliveryDateType,
        extras: extrasWithQuantities,
      })
    );
    setIsDrawerOpen(false);
  };

  const handleQuickAddToCart = (e) => {
    e.stopPropagation();
    if (!effectiveIsOpen) {
      setIsPopupOpen(true);
      return;
    }
    if (!isAvaialable) return;

    const extras = [];

    const vendorName = item?.rawFood?.business?.name || item?.raw_foods?.business?.name || item?.business?.name || item?.store?.name || item?.restaurant?.name || item?.businessName;

    dispatch(
      addToCart({
        restaurantId,
        productId: item.id,
        quantity: minUnits,
        public_price: originalPrice,
        discount: parseFloat(item.discount),
        product_name: item.name,
        businessType,
        businessName: vendorName,
        deliveryDateType: item.deliveryDateType,
        extras,
      })
    );
  };

  const handleDrawerOpen = () => {
    if (!effectiveIsOpen) {
      setIsPopupOpen(true);
      return;
    }
    setQuantity(minUnits);
    setIsDrawerOpen(true);
  };
  const handleDrawerClose = () => setIsDrawerOpen(false);

  return (
    <>
      <div
        onClick={handleDrawerOpen}
        className={`w-full max-w-[300px] bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-200 border border-gray-200 hover:border-black cursor-pointer flex flex-col justify-between overflow-hidden relative ${!effectiveIsOpen || !isAvaialable
          ? "opacity-60 grayscale"
          : ""
          }`}
      >
        <div className="relative w-full">
          {(() => {
            const rawImg = item?.imageUrl || item?.image_url || item?.image || item?.images?.[0]?.url;
            const isWholesaleOrRetail = businessType === "raw_food" || businessType === "wholesale" || businessType === "retail" || businessType === "store";
            const hasValidUploadedImage = rawImg && typeof rawImg === "string" && !rawImg.toLowerCase().includes("chef") && !rawImg.toLowerCase().includes("default");

            if (isWholesaleOrRetail && !hasValidUploadedImage) {
              return (
                <div className="h-[200px] w-full bg-gray-100 flex items-center justify-center border-b border-gray-200 p-4 text-center">
                  <span className="text-gray-500 font-bold text-xl uppercase tracking-wider">{item.name}</span>
                </div>
              );
            }

            return (
              <img
                className="object-cover h-[220px] w-full"
                src={rawImg || "/images/default-dish.jpg"}
                alt={item.name}
              />
            );
          })()}
          {businessType === "restaurant" && item?.deliveryDateType === "same_day" && (
            <div className="absolute font-bold uppercase top-3 right-3 bg-white text-red-600 shadow-md px-2.5 py-1 rounded-lg text-xs">
              Ready today
            </div>
          )}
          {businessType === "restaurant" && item?.deliveryDateType !== "same_day" && (
            <div className="absolute font-bold uppercase top-3 right-3 bg-red-600 text-white shadow-md px-2.5 py-1 rounded-lg text-xs">
              Ready tomorrow
            </div>
          )}
        </div>

        <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
          <div>
            <h3 className="font-bold text-2xl text-gray-900 line-clamp-2 leading-snug">{item.name}</h3>

            {/* {item?.capacity && (
              <p className="text-gray-500 text-sm mt-1 font-medium">
                ({formatCapacityClean(item?.capacity)} {item?.unit || ""})
              </p>
            )} */}

            {/* {item?.packType && parseFloat(item?.containerCost || 0) > 0 && (
              <p className="text-gray-500 text-xs mt-1">
                {(() => {
                  const pt = (item.packType || "").toLowerCase();
                  if (["wraps", "foil", "not applicable", "n/a"].includes(pt)) {
                    return `Pack Type: ${formatString(item.packType)}`;
                  }
                  const displayPortions = item.portionsPerPack || 1;
                  const displayType = formatString(item.packType || "Container");
                  return `${displayPortions} portions in ${displayType} container`;
                })()}
              </p>
            )} */}

            {spicyLabel && (
              <span className="inline-flex items-center gap-1 mt-2 bg-red-50 text-red-600 font-bold text-xs px-2.5 py-0.5 rounded-full border border-red-100">
                🌶️ {spicyLabel}
              </span>
            )}

            {(businessType === "raw_food" || businessType === "wholesale") && (
              <p className="text-gray-600 mt-1 font-medium">
                Sold by {item?.businessName || item?.business?.name || item?.rawFood?.name || item?.store?.name || item?.sellerName || "Seller"}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-2">
            <div>
              <span className="text-black font-extrabold text-2xl">
                <NumberFormat
                  value={roundDisplayPrice(originalPrice)}
                  displayType={"text"}
                  thousandSeparator={true}
                  decimalScale={0}
                  prefix={"₦"}
                />
              </span>
              {!isAvaialable && (
                <p className="text-xs text-red-600 font-bold">Out of Stock</p>
              )}
            </div>

            <button
              onClick={handleQuickAddToCart}
              disabled={isOpen ? !isAvaialable : false}
              className={`p-2.5 rounded-full shadow-md transition-all flex items-center justify-center ${!isOpen
                ? "bg-gray-400 text-gray-200 cursor-pointer"
                : isAvaialable
                  ? "bg-black text-white hover:bg-gray-800 cursor-pointer"
                  : "bg-gray-400 text-gray-200 cursor-not-allowed"
                }`}
            >
              <ShoppingCart size={18} />
            </button>
          </div>
        </div>
      </div>
      {/* Drawer for detailed view */}
      <RightDrawerTemplate
        closeDrawer={handleDrawerClose}
        openDrawer={isDrawerOpen}
        width={450}
        title={businessType === "restaurant" ? "Menu Item" : "Stock Item"}
        sm={true}
      >
        <div className="flex flex-col gap-8 my-4 mt-8">
          <h2 className="text-[22px]">{item.name}</h2>
          <div className="singleRestaurant_img flex">
            {(() => {
              const rawImg = item?.imageUrl || item?.image_url || item?.image || item?.images?.[0]?.url;
              const isWholesaleOrRetail = businessType === "raw_food" || businessType === "wholesale" || businessType === "retail" || businessType === "store";
              const hasValidUploadedImage = rawImg && typeof rawImg === "string" && !rawImg.toLowerCase().includes("chef") && !rawImg.toLowerCase().includes("default");

              if (isWholesaleOrRetail && !hasValidUploadedImage) {
                return (
                  <div className="w-full h-[200px] bg-gray-100 flex items-center justify-center border border-gray-200 p-4 text-center rounded-xl">
                    <span className="text-gray-500 font-bold text-2xl uppercase tracking-wider">{item.name}</span>
                  </div>
                );
              }

              return (
                <img
                  src={rawImg || "/images/default-dish.jpg"}
                  alt={item.name}
                  className="w-full h-[200px] object-cover rounded-xl"
                />
              );
            })()}
          </div>
          <div>
            <div className="space-y-8">
              <p className="text-3xl font-gilroyBold">Description</p>
              <p className="text-[14px] text-[gray]">
                {item.description || "No description available"}
              </p>
              <div className="space-y-3">
                {businessType === "restaurant" && (
                  <>
                    {spicyLabel && (
                      <div className="border-t border-gray-200 pt-3">
                        <p className="font-medium text-[16px] text-gray-700 flex items-center gap-2">
                          Spicy Level:
                          <span className="inline-flex items-center gap-1 bg-red-50 text-red-600 font-bold px-3 py-1 rounded-full border border-red-100">
                            🌶️ {spicyLabel}
                          </span>
                        </p>
                      </div>
                    )}
                    <div className="border-t border-gray-200 pt-3">
                      <p className="font-medium text-[16px] text-gray-700">
                        Ready:{" "}
                        {item?.deliveryDateType === "same_day"
                          ? "today"
                          : "tomorrow"}
                      </p>
                    </div>
                  </>
                )}
              </div>
              {parseFloat(item?.containerCost || 0) > 0 && (
                <div>
                  <Modal
                    open={modalOpen}
                    footer={null}
                    onCancel={() => setModalOpen(false)}
                  >
                    <img
                      src="/images/dish-container.jpg"
                      alt="dish container"
                      className="m-auto w-full h-full"
                    />
                  </Modal>
                </div>
              )}
            </div>
            <div className="w-full mt-8 flex justify-between items-center">
              <p className="my-3 font-bold text-2xl">
                Amount:
                <NumberFormat
                  value={roundDisplayPrice(originalPrice)}
                  displayType={"text"}
                  thousandSeparator={true}
                  decimalScale={0}
                  prefix={" ₦"}
                />
              </p>
              <div className="flex items-center justify_between amount_box py-1 px-6 rounded-2xl">
                <Button
                  shape="circle"
                  size="small"
                  className="justify-center hover:text-black      flex items-center"
                  icon={<MinusOutlined />}
                  onClick={() => setQuantity((q) => Math.max(minUnits, q - 1))}
                />
                <span className="font-bold text-[15px]  ">{quantity}</span>
                <Button
                  shape="circle"
                  size="small"
                  className="justify-center hover:text-black   flex items-center"
                  icon={<PlusOutlined />}
                  onClick={() => setQuantity((q) => q + 1)}
                />
              </div>
            </div>
          </div>
          {/*// Inside the render function of the Drawer, modify the Checkbox to pass the `name`*/}
          <div className="w-full flex justify-end px-4 items-center">
            <Button
              size="large"
              disabled={!isAvaialable}
              onClick={handleAddToCart}
              className={`bg-[green] hover:bg-gray-900 rounded-2xl border border-[green] text-white font-bold ${!isAvaialable ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              +
              <NumberFormat
                value={roundDisplayPrice(calculateTotalPrice())}
                displayType={"text"}
                thousandSeparator={true}
                decimalScale={0}
                prefix={" ₦"}
              />
            </Button>
          </div>
        </div>
      </RightDrawerTemplate>

      <Modal
        open={isPopupOpen}
        footer={null}
        onCancel={() => setIsPopupOpen(false)}
        centered
        width={360}
      >
        <div className="text-center py-4">
          <h3 className="font-gilroyBold text-2xl text-gray-900 uppercase tracking-wide mb-2">
            Opens Tomorrow
          </h3>
          <p className="text-gray-600 text-lg">
            This restaurant is closed. It opens tomorrow at <span className="font-bold text-black">{openTime || "its scheduled time"}</span>.
          </p>
        </div>
      </Modal>
    </>
  );
};
